/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial creation of the ComparisonResultTable component.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically customize
 *                                   field titles based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays comparison data in a table.
 *                      It does not handle sensitive data directly or perform write operations.
 * INTENT: Present version comparison results in a clear, tabular format with visual indicators
 *         for the 'result' metric, making it easy for users to interpret changes between versions,
 *         now with whitelabeling capabilities for field titles.
 * PROMPTER_INSTRUCTIONS: Ensure numerical formatting is consistent and the arrow logic is correct.
 *                        Consider adding tooltips for clarity on metrics if needed.
 * LLM_CONFIDENCE: 100% - Standard table component with conditional rendering for icons and styling.
 */
import React from 'react';
import { ComparisonResult, ProductComparisonDetail } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface ComparisonResultTableProps {
  data: ComparisonResult;
  entityId?: string; // NEW: Add entityId prop for potential future use, though not directly used for comparison fields
}

const ComparisonResultTable: React.FC<ComparisonResultTableProps> = ({ data, entityId = 'versioning' }) => { // NEW: Default entityId
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId]?.title || defaultTitle;

  if (!data || Object.keys(data).length === 0) {
    return <p className="text-muted-foreground">No comparison data available.</p>;
  }

  const formatValue = (value: number) => value.toFixed(2);
  const formatPercentage = (value: number) => value.toFixed(2) + '%';

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="sticky left-0 bg-background z-10">{getFieldTitle('productCode', 'Product Code')}</TableHead>
            <TableHead className="text-center" colSpan={4}>Base Version</TableHead>
            <TableHead className="text-center" colSpan={4}>Alternative Version</TableHead>
            <TableHead className="text-center">{getFieldTitle('result', 'Result')}</TableHead>
          </TableRow>
          <TableRow>
            <TableHead className="sticky left-0 bg-background z-10"></TableHead>
            <TableHead className="bg-green-100">{getFieldTitle('cost', 'Cost')}</TableHead>
            <TableHead className="bg-green-100">{getFieldTitle('price', 'Price')}</TableHead>
            <TableHead className="bg-green-100">{getFieldTitle('profit', 'Profit')}</TableHead>
            <TableHead className="bg-green-100">{getFieldTitle('margin', 'Margin')}</TableHead>
            <TableHead>{getFieldTitle('cost', 'Cost')}</TableHead>
            <TableHead>{getFieldTitle('price', 'Price')}</TableHead>
            <TableHead>{getFieldTitle('profit', 'Profit')}</TableHead>
            <TableHead>{getFieldTitle('margin', 'Margin')}</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(data).map(([productCode, details]) => {
            const resultBG = details.result > 0 ? `bg-green-300` : `bg-red-300`;

            return (
            <TableRow key={productCode}>
              <TableCell className="font-medium sticky left-0 bg-background z-10">{productCode}</TableCell>
              <TableCell className="bg-green-100">${formatValue(details.HEAD.cost)}</TableCell>
              <TableCell className="bg-green-100">${formatValue(details.HEAD.price)}</TableCell>
              <TableCell className="bg-green-100"><b>${formatValue(details.HEAD.profit)}</b></TableCell>
              <TableCell className="bg-green-100">{formatPercentage(details.HEAD.margin)}</TableCell>
              <TableCell>${formatValue(details.LOCAL.cost)}</TableCell>
              <TableCell>${formatValue(details.LOCAL.price)}</TableCell>
              <TableCell><b>${formatValue(details.LOCAL.profit)}</b></TableCell>
              <TableCell>{formatPercentage(details.LOCAL.margin)}</TableCell>
              <TableCell className={"flex items-center justify-center " +  resultBG}>
                {formatValue(details.result)}%
                {details.result > 0 ? (
                  <ArrowUp className="h-4 w-4 text-green-500 ml-1" />
                ) : (
                  <ArrowDown className="h-4 w-4 text-red-500 ml-1" />
                )}
              </TableCell>
            </TableRow>
            )}
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ComparisonResultTable;